import { Input } from '@/shared/components/ui/input';
import React, { useEffect, useRef, useState } from 'react';

/** 수량 소수 자릿수 상한 — 처방 수량은 소수점 4자리까지만 받는다 */
export const QUANTITY_MAX_FRACTION_DIGITS = 4;

/**
 * 수량 입력 문자열 정규화.
 * 빈 칸·마이너스 단독은 유지하고, 소수(1.5, -0.5, 1.)와 음수를 허용한다.
 * 05처럼 의미 없는 선행 0만 제거한다. (0, 0.5, -0은 유지)
 * 소수점 아래가 4자리를 넘으면 입력을 거절한다.
 */
export const normalizeQuantityInput = (raw: string): string | null => {
  if (raw === '') return '';
  if (raw === '-') return '-';

  const negative = raw.startsWith('-');
  const body = negative ? raw.slice(1) : raw;
  if (body === '') return '-';
  if (!/^\d*\.?\d*$/.test(body)) return null;

  const hasDot = body.includes('.');
  const [integerPart = '', fractionPart] = body.split('.');
  if ((fractionPart?.length ?? 0) > QUANTITY_MAX_FRACTION_DIGITS) return null;

  if (integerPart === '') {
    return `${negative ? '-' : ''}.${fractionPart ?? ''}`;
  }

  const normalizedInteger = integerPart.replace(/^0+(?=\d)/, '');
  const normalizedBody = hasDot
    ? `${normalizedInteger}.${fractionPart ?? ''}`
    : normalizedInteger;
  return `${negative ? '-' : ''}${normalizedBody}`;
};

export const quantityInputToNumber = (raw: string): number => {
  if (raw === '' || raw === '.' || raw === '-' || raw === '-.') return 0;
  const value = Number(raw);
  return Number.isFinite(value) ? value : 0;
};

interface QuantityInputProps {
  value: number;
  onChange: (value: number) => void;
  className?: string;
  'aria-label': string;
}

/**
 * 원외·원내 수량.
 *
 * 숫자 0을 controlled value로 그대로 두면 지우기/백스페이스가 즉시 0이 되고,
 * 이어서 치는 숫자가 05처럼 앞에 붙는다. 편집 중에는 빈 칸을 허용한다.
 * 정정용으로 음수 입력도 허용한다.
 */
export const QuantityInput: React.FC<QuantityInputProps> = ({
  value,
  onChange,
  className,
  'aria-label': ariaLabel,
}) => {
  const [text, setText] = useState(() => (value === 0 ? '' : String(value)));
  const focusedRef = useRef(false);

  useEffect(() => {
    if (focusedRef.current) return;
    // Number 왕복(1.230 → 1.23)으로 입력 자릿수를 깎지 않는다
    setText((prev) => {
      if (quantityInputToNumber(prev) === value) return prev;
      return value === 0 ? '' : String(value);
    });
  }, [value]);

  const commit = (raw: string) => {
    const next = normalizeQuantityInput(raw);
    if (next === null) return;
    setText(next);
    const numeric = quantityInputToNumber(next);
    if (numeric !== value) onChange(numeric);
  };

  return (
    <Input
      aria-label={ariaLabel}
      className={className}
      inputMode="decimal"
      type="text"
      value={text}
      onFocus={(event) => {
        focusedRef.current = true;
        event.target.select();
      }}
      onBlur={() => {
        focusedRef.current = false;
        commit(text);
      }}
      onChange={(event) => commit(event.target.value)}
    />
  );
};
